/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo, ReactNode } from "react";

// react-chartjs-2 components
import { Pie } from "react-chartjs-2";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// PieChart configurations
import configs from "examples/Charts/PieChart/configs";
import { Box, Grid, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Declaring props types for PieChart
interface Props {
  icon?: {
    color?: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "light" | "dark";
    component: ReactNode;
  };
  title?: string;
  description?: string | ReactNode;
  height?: string | number;
  chart: {
    labels: string[];
    datasets: {
      label: string;
      backgroundColors: string[];
      data: number[];
    };
  };
  [key: string]: any;
}

function PieChart({ icon, title, description, height, chart }: Props): JSX.Element {
  const { data, options } = configs(chart.labels || [], chart.datasets || {});
  const theme = useTheme();

  const renderChart = (
    <MDBox py={2} pr={2}>
      {title || description ? (
        <MDBox display="flex" px={description ? 1 : 0} pt={description ? 1 : 0}>
          {icon.component && (
            <MDBox
              width="4rem"
              height="4rem"
              bgColor={icon.color || "info"}
              variant="gradient"
              coloredShadow={icon.color || "info"}
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              color="white"
              mt={-5}
              mr={2}
            >
              <Icon fontSize="medium">{icon.component}</Icon>
            </MDBox>
          )}
          <MDBox mt={icon.component ? -2 : 0}>
            {title && <MDTypography variant="h6">{title}</MDTypography>}
            <MDBox mb={2}>
              <MDTypography component="div" variant="button" color="text">
                {description}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      ) : null}
      {useMemo(
        () => (
          <MDBox height={height}>
            <Grid container direction={"column"} gap={2}>
              <Grid item>
                <Pie data={data} options={options} />
              </Grid>
              <Grid item>
                <Box display="flex" flexWrap="wrap" gap={2} mt={2} justifyContent="center">
                  {chart.labels.map((label, i) => {
                    const color = chart.datasets.backgroundColors[i];
                    const bgColor =
                      theme.palette[color as keyof typeof theme.palette]?.main || color;
                    return (
                      <Box key={label} display="flex" alignItems="center">
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            mr: "4px",
                            backgroundColor: bgColor,
                          }}
                        />
                        <Typography fontSize={"12px"}>
                          {label}: {chart.datasets.data[i]}명
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Grid>
            </Grid>
          </MDBox>
        ),
        [chart, height]
      )}
    </MDBox>
  );

  // return title || description ? <Card>{renderChart}</Card> : renderChart;
  return renderChart;
}

// Declaring default props for PieChart
PieChart.defaultProps = {
  icon: { color: "info", component: "" },
  title: "",
  description: "",
  height: "19.125rem",
};

export default PieChart;
